import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { GET_CMS_HAT_THEMES } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useReactQueryErrorReporter } from "./use-error-reporter";

export const useHatThemes = (themeIds = []) => {
  const { error, data, ...rest } = useQuery({
    queryKey: ["hat-themes", themeIds.join(",")],
    queryFn: async () => {
      const resp = await graphQLClient.request(GET_CMS_HAT_THEMES, {
        drafts: false,
        staleTime: 1000 * 60 * 10, // Consider data fresh for 10 minutes (same as production appsync cache duration)
        cacheTime: 1000 * 60 * 60 * 12, // Keep unused data in cache for 12 hours
        gcTime: 1000 * 60 * 60 * 12, // Keep unused data in cache for 12 hours
      });
      return resp.cmsHatThemes;
    },
  });

  useReactQueryErrorReporter("Error fetching hat themes", error, false);

  // Filter themes based on provided themeIds
  const filteredThemes = useMemo(() => {
    if (!themeIds || themeIds.length === 0) return data;
    if (!data || !themeIds?.length) return [];

    // Convert themeIds to strings for comparison since they might be numbers or strings
    const themeIdStrings = themeIds.map((id) => String(id));

    return data.filter((theme) => themeIdStrings.includes(String(theme.id)));
  }, [data, themeIds]);

  return {
    ...rest,
    error,
    themes: filteredThemes,
    allThemes: data || [],
    loading: rest.isLoading,
  };
};
